import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Shield, ShieldCheck, Eye, Users, Trash2, Check, X,
  Clock, ChevronDown, FileText, AlertTriangle, KeyRound
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const roleBadgeStyles = {
  admin: 'bg-red-100 text-red-700 border-red-200',
  editor: 'bg-amber-100 text-amber-700 border-amber-200',
  reader: 'bg-slate-100 text-slate-600 border-slate-200',
};

const roleIcons = {
  admin: ShieldCheck,
  editor: Shield,
  reader: Eye,
};

const statusStyles = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const AdminPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [deleteRequests, setDeleteRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleDropdown, setRoleDropdown] = useState(null);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewingId, setReviewingId] = useState(null);
  const [resetPasswordModal, setResetPasswordModal] = useState(null); // {user_id, user_name, temp_password, user_email}

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, requestsRes] = await Promise.all([
        axios.get(`${API_URL}/auth/users`),
        axios.get(`${API_URL}/delete-requests`),
      ]);
      setUsers(usersRes.data);
      setDeleteRequests(requestsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.patch(`${API_URL}/auth/users/${userId}/role`, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      setRoleDropdown(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleReviewRequest = async (requestId, action) => {
    try {
      await axios.post(`${API_URL}/delete-requests/${requestId}/review`, {
        action,
        comment: reviewComment,
      });
      toast.success(`Delete request ${action === 'approve' ? 'approved' : 'rejected'}`);
      setReviewingId(null);
      setReviewComment('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review request');
    }
  };

  const handleResetPassword = async (userId, userName) => {
    if (!window.confirm(`Reset password for "${userName}"? A temporary password will be generated and they will be forced to change it on next login.`)) return;
    try {
      const res = await axios.post(`${API_URL}/auth/users/${userId}/reset-password`);
      const { temporary_password, user_email, instructions } = res.data;
      
      // Show modal with temporary password
      setResetPasswordModal({
        user_name: userName,
        user_email,
        temp_password: temporary_password,
        instructions
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const pendingCount = deleteRequests.filter(r => r.status === 'pending').length;

  if (loading) {
    return (
      <Layout>
        <div className="p-8 flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-[#2B8FD8] border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8 max-w-6xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Admin Panel</h1>
          <p className="text-slate-500 mt-1">Manage users, roles, and delete requests</p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="h-11" data-testid="admin-tabs">
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="requests" className="flex items-center gap-2">
              <Trash2 className="h-4 w-4" />
              Delete Requests
              {pendingCount > 0 && (
                <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full ml-1">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="px-6 py-4 border-b border-slate-200 rounded-t-xl">
              <h2 className="text-lg font-semibold text-slate-900">User Management</h2>
              <p className="text-sm text-slate-500">Assign roles to control access levels</p>
            </div>
            <div className="divide-y divide-slate-100">
              {users.map((u) => {
                const RoleIcon = roleIcons[u.role] || Eye;
                return (
                  <div key={u.id} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#2B8FD8] flex items-center justify-center text-white font-semibold">
                        {u.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{u.name}</p>
                        <p className="text-xs text-slate-500">{u.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <button
                          onClick={() => setRoleDropdown(roleDropdown === u.id ? null : u.id)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${roleBadgeStyles[u.role]}`}
                        >
                          <RoleIcon className="h-3.5 w-3.5" />
                          {u.role.charAt(0).toUpperCase() + u.role.slice(1)}
                          <ChevronDown className="h-3 w-3" />
                        </button>

                        {roleDropdown === u.id && (
                          <div className="absolute right-0 bottom-full mb-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50 w-40">
                            {['admin', 'editor', 'reader'].map((r) => {
                              const Icon = roleIcons[r];
                              return (
                                <button
                                  key={r}
                                  onClick={() => handleRoleChange(u.id, r)}
                                  disabled={r === u.role}
                                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50 transition-colors ${
                                    r === u.role ? 'text-slate-400 cursor-not-allowed' : 'text-slate-700'
                                  }`}
                                >
                                  <Icon className="h-4 w-4" />
                                  {r.charAt(0).toUpperCase() + r.slice(1)}
                                  {r === u.role && <Check className="h-3.5 w-3.5 ml-auto text-[#2B8FD8]" />}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResetPassword(u.id, u.name)}
                        className="h-7 text-xs text-slate-400 hover:text-amber-600 px-2"
                        title="Reset password"
                        data-testid={`reset-pwd-${u.id}`}
                      >
                        <KeyRound className="h-3.5 w-3.5" />
                      </Button>

                      <span className="text-xs text-slate-400">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  </div>
                );
              })}
              {users.length === 0 && (
                <div className="px-6 py-12 text-center text-slate-400">No users found</div>
              )}
            </div>
          </div>
          </TabsContent>

          {/* Delete Requests Tab */}
          <TabsContent value="requests">
          <div className="space-y-4">
            {deleteRequests.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 px-6 py-12 text-center">
                <Trash2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400">No delete requests</p>
              </div>
            ) : (
              deleteRequests.map((req) => (
                <div key={req.id} className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                        <FileText className="h-5 w-5 text-red-500" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">{req.doc_title}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">Project: {req.project_name}</p>
                        <p className="text-xs text-slate-500">Requested by: {req.requested_by_name}</p>
                        {req.reason && (
                          <p className="text-xs text-slate-600 mt-1 bg-slate-50 px-2 py-1 rounded">
                            <span className="font-medium">Reason:</span> {req.reason}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Clock className="h-3 w-3 text-slate-400" />
                          <span className="text-xs text-slate-400">
                            {new Date(req.created_at).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusStyles[req.status]}`}>
                        {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                      </span>
                    </div>
                  </div>

                  {req.status === 'pending' && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      {reviewingId === req.id ? (
                        <div className="space-y-3">
                          <textarea
                            value={reviewComment}
                            onChange={(e) => setReviewComment(e.target.value)}
                            placeholder="Add a comment (optional)..."
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2B8FD8] focus:border-transparent resize-none"
                            rows={2}
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => handleReviewRequest(req.id, 'approve')}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1.5"
                              size="sm"
                            >
                              <Check className="h-3.5 w-3.5 mr-1" />
                              Approve & Delete
                            </Button>
                            <Button
                              onClick={() => handleReviewRequest(req.id, 'reject')}
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50 text-xs px-3 py-1.5"
                              size="sm"
                            >
                              <X className="h-3.5 w-3.5 mr-1" />
                              Reject
                            </Button>
                            <Button
                              onClick={() => { setReviewingId(null); setReviewComment(''); }}
                              variant="ghost"
                              className="text-slate-500 text-xs px-3 py-1.5"
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setReviewingId(req.id)}
                          variant="outline"
                          className="text-xs"
                          size="sm"
                        >
                          <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
                          Review Request
                        </Button>
                      )}
                    </div>
                  )}

                  {req.status !== 'pending' && req.reviewed_by_name && (
                    <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                      <span className="font-medium">{req.status === 'approved' ? 'Approved' : 'Rejected'}</span> by {req.reviewed_by_name}
                      {req.review_comment && <span> — "{req.review_comment}"</span>}
                      {req.reviewed_at && <span className="ml-2">{new Date(req.reviewed_at).toLocaleString()}</span>}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Password Reset Modal */}
      {resetPasswordModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                <KeyRound className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Password Reset Successful</h3>
                <p className="text-sm text-slate-600">for {resetPasswordModal.user_name}</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-amber-900">Important Security Notice</p>
                  <p className="text-amber-700 text-xs mt-1">
                    The user will be forced to change this password on their next login. Share this securely via your preferred communication channel.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">User Email</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={resetPasswordModal.user_email}
                    readOnly
                    className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700"
                  />
                  <button
                    onClick={() => copyToClipboard(resetPasswordModal.user_email)}
                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-lg transition"
                    title="Copy email"
                  >
                    <svg className="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Temporary Password</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={resetPasswordModal.temp_password}
                    readOnly
                    className="flex-1 px-3 py-2 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg text-sm font-mono font-bold text-amber-900"
                  />
                  <button
                    onClick={() => copyToClipboard(resetPasswordModal.temp_password)}
                    className="px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-lg transition shadow-sm"
                    title="Copy password"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
              <p className="text-xs text-blue-800">
                <strong>Next step:</strong> Share this password with {resetPasswordModal.user_name} via email, Slack, or phone. They will be prompted to change it immediately upon login.
              </p>
            </div>

            <button
              onClick={() => setResetPasswordModal(null)}
              className="w-full mt-4 bg-slate-900 hover:bg-slate-800 text-white py-2.5 rounded-lg font-medium transition"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminPage;
